import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

import { TotemInput } from 'components/TotemInput';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { PAGES } from 'constants/pages';
import { setTokens } from 'data/connection/storage';
import { useAuthenticateUserMutation, useSetPasswordMutation } from 'data/__generated__';
import { isTotadminUser } from 'utils/auth';

const MEDIUM_PASSWORD_REGEX = /^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})/;

export const SetPassword = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);

    const [authenticateUser] = useAuthenticateUserMutation();
    const [setUserPassword] = useSetPasswordMutation();

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setError('Lien invalide');
                setIsTokenValid(false);
                return;
            }
            try {
                const { data } = await authenticateUser({ variables: { token } });
                const result = data?.authenticateUser;
                if (result?.success) {
                    setIsTokenValid(true);
                } else {
                    setError(result?.error || 'Une erreur est survenue');
                    setIsTokenValid(false);
                }
            } catch (e: unknown) {
                const message = e instanceof Error ? e.message : '';
                if (message.includes('invalid token')) {
                    setError('Ce lien est invalide. Veuillez vérifier votre lien.');
                } else if (message.includes('maxAge exceeded')) {
                    setError('Ce lien a expiré. Veuillez contacter un administrateur.');
                } else {
                    setError('Une erreur est survenue lors de la vérification du lien.');
                }
                setIsTokenValid(false);
            }
        };

        verifyToken();
    }, [token, authenticateUser]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFormError(null);

        if (password !== passwordConfirmation) {
            setFormError('Les mots de passe sont différents');
            return;
        }

        if (!password.match(MEDIUM_PASSWORD_REGEX)) {
            setFormError(
                'Le mot de passe doit contenir au moins 6 caractères avec un mix de majuscules, minuscules ou chiffres',
            );
            return;
        }

        const { data } = await setUserPassword({
            variables: { password, token: token! },
        });

        const response = data?.setUserPassword;

        if (response?.success && response.token) {
            if (response.user && isTotadminUser(response.user)) {
                navigate(PAGES.passwordDefined.url);
                return;
            }
            setTokens(response.token, response.refreshToken ?? undefined);
            toast.success('Mot de passe défini avec succès');
            navigate('/');
        } else if (response?.error) {
            setFormError(response.error);
        }
    };

    if (isTokenValid === null) {
        return (
            <Container>
                <Helmet title="Chargement... · TOTADMIN" />
                <Title>Vérification en cours...</Title>
            </Container>
        );
    }

    if (!isTokenValid) {
        return (
            <Container>
                <Helmet title="Erreur · TOTADMIN" />
                <ErrorTitle>{error}</ErrorTitle>
            </Container>
        );
    }

    return (
        <Container>
            <Helmet title="Définir mon mot de passe · TOTADMIN" />
            <Title>Définir mon mot de passe</Title>
            <SetPasswordForm onSubmit={handleSubmit}>
                <TotemInput
                    value={password}
                    placeholder="Mot de passe"
                    type="password"
                    onChange={(value) => setPassword(value)}
                    label="Mot de passe"
                    dataTest="set-password"
                />
                <TotemInput
                    value={passwordConfirmation}
                    placeholder="Confirmez votre mot de passe"
                    type="password"
                    onChange={(value) => setPasswordConfirmation(value)}
                    label="Confirmation"
                    dataTest="set-password-confirm"
                />
                {formError ? <ErrorMessage>{formError}</ErrorMessage> : null}
                <SubmitButton type="submit" data-test="set-password-submit">
                    Valider
                </SubmitButton>
            </SetPasswordForm>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Title = styled.h1`
    text-transform: uppercase;
    color: ${({ theme }) => theme.textColor};
    letter-spacing: 0.7px;
    font-size: 40px;
`;

const ErrorTitle = styled.h1`
    color: ${({ theme }) => theme.errorColor};
    font-size: 24px;
    text-align: center;
    max-width: 500px;
`;

const ErrorMessage = styled.span`
    color: ${({ theme }) => theme.errorColor};
`;

const SetPasswordForm = styled.form`
    display: flex;
    flex-direction: column;
    width: 400px;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const SubmitButton = styled(TotemPrimaryButton)`
    margin-top: 15px;
`;
